import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ValidatorForm } from "react-material-ui-form-validator";
import axios from "../../../../axios-order";
import MonthDatePicker from "../../../../components/ui/monthWiseDatePicker";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import Modal from "react-bootstrap/Modal";
import LoadingButton from "@mui/lab/LoadingButton";
import { canRead, canWrite } from "../../../../utils/privilegeUtils";

// ── Design tokens (identical palette to ItcAvailReport) ──────────────────────
const C = {
    navy: "#1a3f6f",
    accent: "#1a6fc4",
    accentLight: "#e8f1fb",
    white: "#ffffff",
    surface: "#f7f9fc",
    border: "#dde3ec",
    borderLight: "#eef1f6",
    text: "#1a3f6f",
    textSub: "#4a6080",
    textMuted: "#8298b2",
    success: { bg: "#edf8f1", text: "#1a6e3e", dot: "#28a55f" },
};

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatPeriod(period) {
    if (!period) return "—";
    const mm = parseInt(period.slice(0, 2)) - 1;
    const yy = period.slice(2);
    return `${monthNames[mm]} ${yy}`;
}

function formatDateTime(dt) {
    if (!dt) return "—";
    const cleaned = dt.replace("Z", "");
    const [date, time] = cleaned.includes("T") ? cleaned.split("T") : cleaned.split(" ");
    return (
        <span>
            <span style={{ color: C.text, fontWeight: "500" }}>{date}</span>
            <span style={{ color: C.textMuted }}> · {time}</span>
        </span>
    );
}

const DocIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────
export default function ItcReversalReport() {
    const [handleFromDate, setHandleFromDate] = useState("");
    const [handleToDate, setHandleToDate] = useState("");
    const [handleFromDateToDisplay, setHandleFromDateToDisplay] = useState(null);
    const [handleToDateToDisplay, setHandleToDateToDisplay] = useState(null);
    const [trigarLoader, setTrigarLoader] = useState(false);
    const [trigarForErrorMessage, setTrigarForErrorMessage] = useState(false);
    const [tdsHistoryTable] = useState(true);
    const [triggerButton] = useState(true);
    const [triggerModelFromToDate, setTriggerModelFromToDate] = useState(false);
    const [tdsReconHistoryData, setTdsReconHistoryData] = useState([]);
    const [loaderForTable, setLoaderForTable] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [message] = useState("");
    const [responseMessage, setResponseMessage] = useState("");
    const [loaderSubmitButton, setLoaderSubmitButton] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);

    const history = useHistory();

    const handleBack = () => history.push("/reportCards");

    // ── Snackbar helpers ──────────────────────────────────────────────────────
    const handleSnackbarOpen = (msg, severity) => {
        setSnackbarOpen(true);
        setSnackbarSeverity(severity);
        setResponseMessage(msg);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarSeverity("success");
        setResponseMessage("");
    };

    // ── Data fetch ────────────────────────────────────────────────────────────
    const getItcReportRecords = () => {
        setLoaderForTable(true);
        axios
            .post("/get_itc_reversal_report_records", {
                business_entity_id: localStorage.getItem("business_entity_id"),
            })
            .then((res) => {
                setLoaderForTable(false);
                setTdsReconHistoryData(res.data.records);
            })
            .catch(() => setLoaderForTable(false));
    };

    useEffect(() => { getItcReportRecords(); }, []);

    // ── Date handlers ─────────────────────────────────────────────────────────
    const functionToHandleFromDate = (v) => {
        const pad = "00";
        const yy = v.getFullYear().toString();
        const mm = (v.getMonth() + 1).toString();
        setHandleFromDate(`${(pad + mm).slice(-2)}${yy}`);
        setHandleFromDateToDisplay(v);
    };

    const functionToHandleToDate = (v) => {
        const pad = "00";
        const year = v.getFullYear().toString();
        const month = (v.getMonth() + 1).toString();
        // eslint-disable-next-line no-unused-vars
        const lastDay = new Date(v.getFullYear(), v.getMonth() + 1, 0);
        setHandleToDate(`${(pad + month).slice(-2)}${year}`);
        setHandleToDateToDisplay(v);
    };

    // ── Modal open/close ──────────────────────────────────────────────────────
    const handleOpenAndCloseModel = () => {
        setHandleFromDateToDisplay(null);
        setHandleToDateToDisplay(null);
        setTriggerModelFromToDate(false);
        setTrigarLoader(false);
        setTrigarForErrorMessage(false);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const onSubmitHandlerForData = () => {
        setLoaderSubmitButton(true);
        axios
            .post("/create_itc_reversal_report_summary", {
                business_entity_id: localStorage.getItem("business_entity_id"),
                start_period: handleFromDate,
                end_period: handleToDate,
                org_gstin: localStorage.getItem("gst"),
            })
            .then((res) => {
                handleSnackbarOpen(res.data.message, res.data.status ? "success" : "error");
                getItcReportRecords();
                handleOpenAndCloseModel();
            })
            .catch(() => {
                getItcReportRecords();
                handleOpenAndCloseModel();
            })
            .finally(() => setLoaderSubmitButton(false));
    };

    // ── Navigate to detail ────────────────────────────────────────────────────
    const updatedShowDetails = (item) => {
        history.push({
            pathname: `/itcRevarsalSummary/${item.recon_id}`,
            state: { items: item },
        });
    };

    const handleTDSREconciliationButtonEvent = () => setTriggerModelFromToDate(true);

    // ── Styles ────────────────────────────────────────────────────────────────
    const thStyle = {
        padding: "11px 18px",
        textAlign: "left",
        fontSize: "11px",
        fontWeight: "600",
        letterSpacing: "0.05em",
        color: "#ffffff",
        background: C.navy,
        borderBottom: "1px solid #16355c",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
    };

    const tdStyle = {
        padding: "8px 18px",
        fontSize: "13px",
        color: C.text,
        borderBottom: `1px solid ${C.borderLight}`,
    };

    // ── Table body renderer ───────────────────────────────────────────────────
    const renderTableBody = () => {
        if (loaderForTable) {
            return Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                    {[25, 20, 30, 15, 10].map((w, j) => (
                        <td key={j} style={tdStyle}>
                            <div style={{ height: "14px", borderRadius: "4px", background: C.borderLight, width: `${w}%`, animation: "pulse 1.4s ease-in-out infinite" }} />
                        </td>
                    ))}
                </tr>
            ));
        }

        if (!tdsReconHistoryData || tdsReconHistoryData.length === 0) {
            return (
                <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center", padding: "52px 20px" }}>
                        <div style={{ fontSize: "28px", marginBottom: "10px", opacity: 0.4 }}>📄</div>
                        <div style={{ fontWeight: "500", marginBottom: "4px", color: C.textSub }}>No reports found</div>
                        <div style={{ fontSize: "12px", color: C.textMuted }}>
                            Generate your first ITC Reversal report to get started.
                        </div>
                    </td>
                </tr>
            );
        }

        return tdsReconHistoryData.map((item, i) => (
            <tr
                key={i}
                onMouseEnter={() => setHoveredRow(i)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{ background: hoveredRow === i ? C.surface : C.white, transition: "background 0.1s" }}
            >
                {/* From Period */}
                <td style={tdStyle}>
                    <span style={{ fontWeight: "600" }}>
                        {formatPeriod(item.from_tax_period)}
                    </span>
                </td>
                {/* To Period */}
                <td style={tdStyle}>
                    <span style={{ fontWeight: "600" }}>
                        {formatPeriod(item.to_tax_period)}
                    </span>
                </td>
                {/* Run Date Time */}
                <td style={tdStyle}>
                    <span style={{ fontSize: "12px", color: C.textMuted, fontVariantNumeric: "tabular-nums" }}>
                        {formatDateTime(item.run_date_time)}
                    </span>
                </td>
                {/* Status */}
                <td style={tdStyle}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "5px", fontSize: "11px", fontWeight: "600", background: C.success.bg, color: C.success.text }}>
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: C.success.dot, flexShrink: 0 }} />
                        {item.report_status || "Generated"}
                    </span>
                </td>
                {/* Action */}
                <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button
                        onClick={() => updatedShowDetails(item)}
                        style={{ padding: "5px 14px", borderRadius: "6px", whiteSpace: "nowrap", border: `1px solid ${hoveredRow === i ? C.accent : C.border}`, background: hoveredRow === i ? C.accentLight : C.white, color: hoveredRow === i ? C.accent : C.textSub, fontSize: "12px", fontWeight: "500", cursor: "pointer", transition: "all 0.15s" }}
                    >
                        View report →
                    </button>
                </td>
            </tr>
        ));
    };

    const totalCount = tdsReconHistoryData ? tdsReconHistoryData.length : 0;

    return (
        <React.Fragment>
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

            <div style={{ padding: "28px 32px", background: C.surface, fontFamily: "inherit" }}>

                {/* ── Page header ── */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(13,33,55,0.06)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <button
                            onClick={handleBack}
                            title="Back to Reports"
                            style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.textSub, fontSize: "14px", flexShrink: 0 }}
                        >←</button>
                        <div style={{ width: "42px", height: "42px", borderRadius: "10px", background: C.accentLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <DocIcon />
                        </div>
                        <div>
                            <div style={{ fontSize: "23px", fontWeight: "700", color: C.text, lineHeight: 1.2 }}>ITC Reversal Report</div>
                            <div style={{ fontSize: "12px", color: C.textMuted, marginTop: "4px" }}>Input tax credit reversal records</div>
                        </div>
                    </div>
                    {triggerButton && canWrite("ITC REVERSAL REPORT") && (
                        <button
                            onClick={handleTDSREconciliationButtonEvent}
                            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 18px", height: "36px", borderRadius: "8px", background: C.navy, color: C.white, border: "none", fontSize: "13px", fontWeight: "500", cursor: "pointer" }}
                        >
                            <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
                            New ITC Report
                        </button>
                    )}
                </div>

                {/* ── Metrics card ── */}
                {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "20px" }}>
                    {[
                        { label: "Total Reports", value: totalCount, borderColor: C.accent, badge: totalCount > 0 },
                        { label: "Generated", value: tdsReconHistoryData ? tdsReconHistoryData.filter(r => r.report_status).length : 0, borderColor: "#28a55f", badge: false },
                        { label: "Periods Covered", value: tdsReconHistoryData ? new Set(tdsReconHistoryData.map(r => r.from_tax_period)).size : 0, borderColor: "#8298b2", badge: false },
                    ].map(({ label, value, borderColor, badge }) => (
                        <div key={label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", boxShadow: "0 1px 3px rgba(13,33,55,0.05)", borderTop: `3px solid ${borderColor}` }}>
                            <div style={{ fontSize: "26px", fontWeight: "700", color: C.text, lineHeight: 1, letterSpacing: "-0.5px" }}>{value}</div>
                            <div style={{ fontSize: "11px", color: C.textMuted, marginTop: "5px", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                            {badge && <div style={{ display: "inline-block", marginTop: "8px", fontSize: "10px", padding: "2px 8px", borderRadius: "12px", background: C.success.bg, color: C.success.text, fontWeight: "600" }}>All generated</div>}
                        </div>
                    ))}
                </div> */}

                {/* ── Table card ── */}
                {tdsHistoryTable && (
                    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden", boxShadow: "0 1px 3px rgba(13,33,55,0.05)" }}>

                        {/* Scrollable table */}
                        <div style={{ maxHeight: "480px", overflowY: "auto", overflowX: "hidden" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                                <colgroup>
                                    <col style={{ width: "18%" }} />
                                    <col style={{ width: "18%" }} />
                                    <col style={{ width: "30%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "14%" }} />
                                </colgroup>
                                <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                    <tr>
                                        <th style={thStyle}>From Period</th>
                                        <th style={thStyle}>To Period</th>
                                        <th style={thStyle}>Run Date &amp; Time</th>
                                        <th style={thStyle}>Status</th>
                                        <th style={{ ...thStyle, textAlign: "center" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>{renderTableBody()}</tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        {!loaderForTable && tdsReconHistoryData && tdsReconHistoryData.length > 0 && (
                            <div style={{ padding: "10px 18px", borderTop: `1px solid ${C.borderLight}`, fontSize: "11px", color: C.textMuted, background: C.white }}>
                                Showing {tdsReconHistoryData.length} record{tdsReconHistoryData.length !== 1 ? "s" : ""}
                            </div>
                        )}
                    </div>
                )}

            </div>

           {/* ── Modal ── */}
            {canWrite("ITC REVERSAL REPORT") && <Modal size="lg" centered show={triggerModelFromToDate} onHide={handleOpenAndCloseModel}>
                <Modal.Header closeButton style={{ background: C.navy, borderBottom: "none", padding: "16px 22px" }}>
                    <Modal.Title style={{ fontSize: "18px", fontWeight: "600", color: "#ffffff" }}>
                        Generate New ITC Reversal Report
                    </Modal.Title>
                </Modal.Header>

                <ValidatorForm useRef="form" onSubmit={onSubmitHandlerForData} onError={(errors) => console.log(errors)}>
                    <Modal.Body style={{ padding: "22px 22px 10px" }}>
                        <div style={{ marginBottom: "18px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: "600", color: C.textSub, marginBottom: "6px", display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        From Month <span style={{ color: "#e53e3e" }}>*</span>
                                    </label>
                                    <MonthDatePicker
                                        placeholder="Month & Year"
                                        validators={["required"]}
                                        errorMessages={["This field is required"]}
                                        value={handleFromDateToDisplay}
                                        onChange={(v) => functionToHandleFromDate(v)}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: "600", color: C.textSub, marginBottom: "6px", display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        To Month <span style={{ color: "#e53e3e" }}>*</span>
                                    </label>
                                    <MonthDatePicker
                                        placeholder="Month & Year"
                                        minDateForDatepicker={handleFromDateToDisplay}
                                        validators={["required"]}
                                        errorMessages={["This field is required"]}
                                        value={handleToDateToDisplay}
                                        onChange={(v) => functionToHandleToDate(v)}
                                    />
                                </div>
                            </div>
                        </div>

                        {trigarLoader && (
                            <div style={{ fontWeight: "600", color: "green", padding: "5px" }}>
                                Please wait recon is in progress
                            </div>
                        )}
                        {trigarForErrorMessage && (
                            <div style={{ color: "red", fontWeight: "700" }}>
                                No Records Present!
                            </div>
                        )}
                    </Modal.Body>

                    <Modal.Footer style={{ padding: "12px 22px", gap: "8px", borderTop: `1px solid ${C.borderLight}` }}>
                        <button
                            type="button"
                            onClick={handleOpenAndCloseModel}
                            style={{ padding: "0 18px", height: "36px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, fontSize: "13px", color: C.textSub, cursor: "pointer" }}
                        >
                            Cancel
                        </button>
                        <LoadingButton
                            type="submit"
                            size="small"
                            loading={loaderSubmitButton}
                            variant="contained"
                            style={{ height: "36px", minWidth: "140px", borderRadius: "8px", background: C.navy, fontSize: "13px", fontWeight: "600", textTransform: "none", boxShadow: "none" }}
                        >
                            Generate Report
                        </LoadingButton>
                    </Modal.Footer>
                </ValidatorForm>
            </Modal>}

            {/* ── Snackbar ── */}
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
                style={{ maxWidth: "340px" }}
            >
                <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbarSeverity}>
                    {responseMessage}
                    {message}
                </MuiAlert>
            </Snackbar>
        </React.Fragment>
    );
}